import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import Logo from '../Logo';
import Button from '../Button';
import TextInput from '../TextInput';
import loginBg from '../assets/login_bg.jpg';

/**
 * Login layout mirrors the Léo Delivery backoffice login: a centered white card
 * over a background, with the logo, a panel badge, the credential form, a
 * full-width submit and a footer. Brand/colors are Kardume's own.
 *
 * With `withBackground`, the screen uses the Kardume city photo (assets/bg_splash)
 * behind a navy overlay - used on the Administração (merchant) login.
 */

const Screen = styled.div<{ $image?: string }>`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  ${(p) =>
    p.$image
      ? css`
          background-image: linear-gradient(rgba(16, 39, 53, 0.5), rgba(16, 39, 53, 0.72)),
            url(${p.$image});
          background-size: cover;
          background-position: center;
        `
      : css`
          background-color: ${p.theme.background};
          background-image: radial-gradient(at 0% 0%, ${p.theme.primary}14 0, transparent 50%),
            radial-gradient(at 100% 0%, ${p.theme.petrol}14 0, transparent 50%),
            radial-gradient(at 50% 100%, ${p.theme.accent}0f 0, transparent 50%);
        `}
`;

const Card = styled.form`
  background: ${(p) => p.theme.surface};
  padding: 48px;
  border-radius: ${(p) => p.theme.radiusLg};
  width: 100%;
  max-width: 460px;
  border: 1px solid ${(p) => p.theme.border};
  box-shadow: 0 24px 45px -12px rgba(16, 39, 53, 0.35), 0 10px 10px -5px rgba(16, 39, 53, 0.06);
  text-align: center;
`;

const LogoWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const Badge = styled.div`
  display: inline-block;
  padding: 6px 20px;
  background: ${(p) => p.theme.primary}14;
  color: ${(p) => p.theme.primary};
  border-radius: ${(p) => p.theme.radiusPill};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 18px;
`;

const Title = styled.h2`
  color: ${(p) => p.theme.textPrimary};
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  color: ${(p) => p.theme.textMuted};
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 28px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
  margin-bottom: 1.25rem;
`;

const ErrorBox = styled.div`
  padding: 0.65rem 0.85rem;
  border-radius: ${(p) => p.theme.radius};
  font-size: 0.85rem;
  color: ${(p) => p.theme.error};
  background: ${(p) => p.theme.error}14;
  text-align: left;
`;

const ForgotLink = styled.button`
  margin-top: 1rem;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.primary};
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Hint = styled.p`
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

const ForgotBox = styled.div`
  margin-top: 1rem;
  padding: 0.9rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.textMuted}0F;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ForgotIntro = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: ${(p) => p.theme.textMuted};
`;

const SuccessBox = styled.div`
  padding: 0.65rem 0.85rem;
  border-radius: ${(p) => p.theme.radius};
  font-size: 0.85rem;
  color: ${(p) => p.theme.success};
  background: ${(p) => p.theme.success}14;
  text-align: left;
`;

const SignUpRow = styled.p`
  margin: 1.25rem 0 0;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textMuted};
`;

const SignUpLink = styled.a`
  color: ${(p) => p.theme.primary};
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  margin-top: 32px;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

interface LoginScreenProps {
  /** Kept for API compatibility; shown as the card subtitle. */
  panelTitle?: string;
  panelSubtitle: string;
  /** Panel name badge, e.g. "Administração" / "Backoffice". */
  formTitle: string;
  /** Show a photo background behind the card (Administração login). */
  withBackground?: boolean;
  /**
   * Optional background image URL. Defaults to the bundled Kardume city photo.
   * The Administração login uses a free Unsplash "experiences" photo
   * (rooftop celebration with city view - Vitaly Gariev, Unsplash License).
   */
  backgroundUrl?: string;
  onSubmit: (email: string, password: string) => Promise<void> | void;
  /**
   * Dispara a redefinição de senha do painel. Quando ausente, o link continua
   * apenas orientando a procurar o suporte - que era o comportamento antigo,
   * mantido para quem ainda não tem o fluxo no backend.
   */
  onForgotPassword?: (email: string) => Promise<void>;
  /**
   * Quando informado, mostra o atalho para o autocadastro (wizard de onboarding
   * do lojista). Só a Administração usa - o Backoffice não tem autocadastro.
   */
  signUpHref?: string;
  /** Texto do atalho de autocadastro. */
  signUpLabel?: string;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({
  panelSubtitle,
  formTitle,
  withBackground = false,
  backgroundUrl,
  onSubmit,
  onForgotPassword,
  signUpHref,
  signUpLabel = 'Cadastre sua loja',
}) => {
  const bgImage = withBackground ? backgroundUrl || loginBg : undefined;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForgotHint, setShowForgotHint] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);

  const openForgot = () => {
    setForgotError(null);
    setForgotSent(false);
    // Aproveita o que a pessoa ja digitou: quem clica aqui quase sempre acabou de
    // tentar entrar com o proprio e-mail.
    setForgotEmail(email.trim());
    setShowForgotHint(true);
  };

  const handleForgot = async () => {
    if (!onForgotPassword || forgotLoading) return;
    setForgotError(null);
    setForgotLoading(true);
    try {
      await onForgotPassword(forgotEmail.trim());
      setForgotSent(true);
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : 'Não foi possível enviar as instruções.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      await onSubmit(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível entrar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen $image={bgImage}>
      <Card onSubmit={handleSubmit}>
        <LogoWrap>
          <Logo vertical height={120} />
        </LogoWrap>

        <Badge>{formTitle}</Badge>

        <Title>Acesse sua conta</Title>
        <Subtitle>{panelSubtitle}</Subtitle>

        {error && <ErrorBox style={{ marginBottom: '1rem' }}>{error}</ErrorBox>}

        <Fields>
          <TextInput
            label="E-mail"
            type="email"
            autoComplete="username"
            placeholder="voce@empresa.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
          />
          <TextInput
            label="Senha"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            required
          />
        </Fields>

        <Button type="submit" fullWidth isLoading={loading} disabled={!email || !password}>
          Entrar
        </Button>

        <ForgotLink type="button" onClick={openForgot} disabled={loading}>
          Esqueci minha senha
        </ForgotLink>

        {showForgotHint && !onForgotPassword && (
          <Hint>Entre em contato com o suporte do Kardume Urbano para redefinir seu acesso.</Hint>
        )}

        {showForgotHint && onForgotPassword && (
          <ForgotBox>
            {forgotSent ? (
              /*
               * Mensagem deliberadamente generica: confirmar que a conta existe
               * transformaria esta tela em um verificador de e-mails validos.
               */
              <SuccessBox>
                Se houver uma conta com esse e-mail, enviamos uma senha temporária para ele. No
                próximo acesso será pedido para você definir uma nova senha.
              </SuccessBox>
            ) : (
              <>
                <ForgotIntro>
                  Informe seu e-mail de acesso. Enviaremos uma senha temporária para você entrar e
                  cadastrar uma nova.
                </ForgotIntro>
                {forgotError && <ErrorBox>{forgotError}</ErrorBox>}
                <TextInput
                  label="E-mail"
                  type="email"
                  autoComplete="username"
                  placeholder="voce@empresa.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  disabled={forgotLoading}
                />
                <Button
                  type="button"
                  fullWidth
                  variant="secondary"
                  isLoading={forgotLoading}
                  disabled={!forgotEmail}
                  onClick={handleForgot}
                >
                  Enviar instruções
                </Button>
              </>
            )}
          </ForgotBox>
        )}

        {signUpHref && (
          <SignUpRow>
            Ainda não tem cadastro? <SignUpLink href={signUpHref}>{signUpLabel}</SignUpLink>
          </SignUpRow>
        )}

        <Footer>&copy; {new Date().getFullYear()} Kardume Urbano · Acesso restrito.</Footer>
      </Card>
    </Screen>
  );
};

export default LoginScreen;
